import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import useLocationGroups from "../../data/useLocationGroups";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ILocationGroup } from "../../models/LocationGroup";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  name: string,
  description?: string | null,
  order: number,
};

interface LocationGroupsModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  locationGroup?: ILocationGroup;
  defaultOrder: number;
}

export default function LocationGroupsModal({ open, setOpen, locationGroup, defaultOrder }: LocationGroupsModalProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Location Groups");
  const { deleteHandlers } = useDefaultCRUDHandlers("Location Groups");
  const { update, create, remove } = useLocationGroups();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset } = useForm<FormValues>();

  const onSubmit = (data: any) => {

    const updatedLocationGroup = {
      ...locationGroup,
      ...data,
      // Carried through the form untouched - the position is changed with the move buttons on the
      // table, not typed in here - but the hidden input hands it back as a string and the column is
      // an int.
      order: Number(data.order),
    };

    if (locationGroup !== undefined) {
      update.mutate(updatedLocationGroup, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      create.mutate(updatedLocationGroup, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  }

  const handleDelete = () => {
    remove.mutate(locationGroup?.id!, {
      onSuccess: (data) => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  }

  useEffect(() => {

    if (locationGroup !== undefined) {
      reset({
        name: locationGroup.name,
        description: locationGroup.description ?? "",
        order: locationGroup.order
      });
    } else {
      reset({
        name: "",
        description: "",
        order: defaultOrder
      })
    }
  }, [locationGroup, open, reset, defaultOrder]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={"Delete Section"} message={`Are you sure you want to delete this section?\n Any locations assigned to it will be unassigned. This action cannot be undone.`}
        confirmButtonText={"Delete"} onConfirm={handleDelete} isLoading={false}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{`${locationGroup ? "Edit" : "Add"} a Section`}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <input type="hidden" {...register("order")} />

              <TextInput label="Name" registerName="name" register={register} />

              <TextInput label="Description" registerName="description" register={register} />

              <ButtonInput label={`${locationGroup ? "Save Changes" : "Create"}`} isSubmit={true} onClick={undefined} classes={undefined} />
              {locationGroup !== undefined && (
                <ButtonInput label={"Delete"} isSubmit={false} onClick={() => (setDeleteOpen(true))} classes={"!bg-red-500 hover:!bg-red-600"} />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
